import { Component, OnInit, Inject, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CityService } from '../../services/city.service';
import { CommentService } from '../../services/comment.service';
import { ContactService } from '../../services/contact.service';
import { CRMService } from '../../services/crm.service';
import { EmployeeService } from '../../services/employees.service';
import { ERPService } from '../../services/erp.service';
import { NotificationService } from '../../services/notification.service';
import {
    City
    , Comment
    , Contact
    , Customer
    , CustomerFinancials
    , CustomerInitiative
    , CustomerInitiativeMap
    , CustomerStatus
    , Employee
} from '../../models/models';
import { CityDropdown } from '../city-dropdown/city-dropdown.component';

@Component({
    selector: 'add-customer',
    templateUrl: './add-customer.component.html',
    standalone: false
})
export class AddCustomer implements OnInit, OnChanges {
    @Input() customerId: number = 0;

    @Output() savedCustomer: EventEmitter<Customer> = new EventEmitter<Customer>();

    public selectedCustomerId: number = 0;
    public selectedCustomer: Customer = {} as Customer;
    public cityPlaceholder: string = "Select a City";

    public contacts: Contact[] = [];
    public salesReps: Employee[] = [];
    public opsReps: Employee[] = [];
    public accountReps: Employee[] = [];
    public initiatives: CustomerInitiative[] = [];
    public financials: CustomerFinancials[] = [];
    public customerStatusList: CustomerStatus[] = [];
    public crmNotes: Comment = {} as Comment;
    public city: City = {} as City;
    public operationalNotes: Comment = {} as Comment;
    public customerForm: UntypedFormGroup = new UntypedFormGroup({});
    public saveMessage: string = '';
    public submitted: boolean = false;
    public phoneMask: string = "(999) 000-0000";
    public isCRMAdmin: boolean = false;
    public isSavingCustomer: boolean = false;
    public isSavingContact: boolean = false;
    public customerStatus: string = 'Lead';
    public customerStatusId: number = 0;

    private usPhoneMask: string = '(999) 000-0000';
    private mexicoPhoneMask: string = '99999-999-999-9999';
    private selectedContactId: number = 0;

    constructor(private cityService: CityService, private commentService: CommentService, private contactService: ContactService,
        private crmService: CRMService, private employeeService: EmployeeService,
        private erpService: ERPService, private notificationService: NotificationService,
        public dialog: MatDialog, private formBuilder: UntypedFormBuilder, private router: Router) {
    }

    ngOnInit() {
        this.buildForm();
        this.getEmployees();
        this.getCustomerInitiatives();
        this.getCustomerStatusList();
        this.isCRMAdmin = this.getIsCRMAdmin();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.customerId == null || changes.customerId.currentValue == null || changes.customerId.currentValue.toString() == '') return;

        this.selectedCustomerId = parseInt(changes.customerId.currentValue);
        this.getCustomer();
        this.getCustomerFinancials();
    }

    buildForm() {
        this.customerForm = this.formBuilder.group({
            nameCtrl: ['', Validators.required],
            salesRepCtrl: [''],
            opsRepCtrl: [''],
            accountRepCtrl: [''],
            contactNameCtrl: ['Main', Validators.required],
            contactPhoneCtrl: [''],
            contactEmailCtrl: [''],
            billToIndCtrl: [false],
            shipperIndCtrl: [false],
            consigneeIndCtrl: [false],
            activeIndCtrl: [true],
            retiredIndCtrl: [false],
            addressOneCtrl: ['', Validators.required],
            addressTwoCtrl: [''],
            cityCtrl: ['', Validators.required],
            statusCtrl: ['', Validators.required],
            zipCtrl: ['', Validators.required],
            initiativesCtrl: [''],
            freightCtrl: [0],
            creditLimitAmountCtrl: [0],
            crmNotesCtrl: [''],
            operationalNotesCtrl: [''],
            isParentCtrl: [false],
            revenueBaseLineCtrl: [0]
        });

        //this.customerForm.controls['cityCtrl'].valueChanges.subscribe(result => {
        //    if (result == null || typeof result === 'string') return;

        //    var city = result as City;

        //    if (city.countryID == 113) {
        //        this.phoneMask = this.mexicoPhoneMask;
        //    } else {
        //        this.phoneMask = this.usPhoneMask;
        //    }
        //});
    }

    getEmployees() {
        this.employeeService.getEmployeesByGroupName('Sales').subscribe(result => {
            this.salesReps = result;
        });

        this.employeeService.getEmployeesByGroupName('Ops Rep').subscribe(result => {
            this.opsReps = result;
        });

        this.employeeService.getEmployeesByGroupName('Account Managers').subscribe(result => {
            this.accountReps = result;
        });
    }

    private getIsCRMAdmin() {
        var employeeRoles = localStorage.getItem('EmployeeRoles');

        //console.log(employeeRoles);
        if (employeeRoles != null && employeeRoles.length > 0) {
            if (employeeRoles.indexOf('CRM Admin') > -1) return true;
        }

        return false;
    }

    getCustomerInitiatives() {
        this.crmService.getCustomerInitiatives().subscribe(result => {
            this.initiatives = result;
        });
    }

    getCustomerStatusList() {
        this.crmService.getCustomerStatuses().subscribe(result => {
            this.customerStatusList = result;
            this.customerStatusList = this.customerStatusList.filter(s => s.abbreviation == "SW Lead" || s.abbreviation == "Unqualified Lead");
            if (this.customerId < 1) {
                this.customerStatusId = this.customerStatusList.filter(s => s.abbreviation == "Unqualified Lead")[0].customerStatusId;
                this.customerForm.controls['statusCtrl'].setValue(this.customerStatusId);
            }
        });
    }

    getCustomer() {
        if (this.selectedCustomerId < 1) return;

        this.crmService.getCustomerById(this.selectedCustomerId).subscribe(result => {
            this.selectedCustomer = result;
            this.getCity(this.selectedCustomer.cityId);
            this.setCustomerFormValues();
            this.getComments();
        });

        this.contactService.getContactsByTableIdType(this.selectedCustomerId, 'customer').subscribe(result => {
            this.contacts = result;
            var defaultContact = this.contacts.find(c => c.defaultContactInd == true);

            if (defaultContact == null) return;

            this.selectedContactId = defaultContact.contactID;
            this.customerForm.controls['contactNameCtrl'].setValue(defaultContact.fullName);
            this.customerForm.controls['contactPhoneCtrl'].setValue(defaultContact.phone);
            this.customerForm.controls['contactEmailCtrl'].setValue(defaultContact.email);
        });

        this.crmService.getInitiativeMapsByCustomerId(this.selectedCustomerId).subscribe(result => {
            let initiativeIds: number[] = [];

            result.forEach(i => {
                initiativeIds.push(i.customerInitiativeID);
            });

            this.customerForm.controls['initiativesCtrl'].setValue(initiativeIds);
        });
    }

    private getCity(cityID: number) {
        this.cityService.getCityByID(cityID).subscribe(result => {
            this.city = result.resultCollection[0];
            this.customerForm.controls['cityCtrl'].setValue(this.city);
            this.customerForm.controls['zipCtrl'].setValue(this.city.zip);
        });
    }

    private getCustomerFinancials() {
        if (this.selectedCustomerId < 1) return;

        this.crmService.getCustomerFinancialsByID(this.selectedCustomerId).subscribe(result => {
            this.financials = result;
        });
    }

    public isStatusEdit(abbr) {
        return abbr == "SW Lead" || abbr == "Lead";
    }

    private setCustomerFormValues() {
        if (this.selectedCustomer == null || this.selectedCustomer.customerID == null || this.selectedCustomer.customerID < 1) return;

        this.customerForm.controls['nameCtrl'].setValue(this.selectedCustomer.name);
        this.customerForm.controls['salesRepCtrl'].setValue(this.selectedCustomer.salesEmployeeID);
        this.customerForm.controls['opsRepCtrl'].setValue(this.selectedCustomer.opsRepId);
        this.customerForm.controls['accountRepCtrl'].setValue(this.selectedCustomer.accountRepId);
        this.customerForm.controls['billToIndCtrl'].setValue(this.selectedCustomer.billToInd);
        this.customerForm.controls['shipperIndCtrl'].setValue(this.selectedCustomer.shipperInd);
        this.customerForm.controls['consigneeIndCtrl'].setValue(this.selectedCustomer.consigneeInd);
        this.customerForm.controls['activeIndCtrl'].setValue(this.selectedCustomer.activeInd);
        this.customerForm.controls['retiredIndCtrl'].setValue(this.selectedCustomer.retiredInd);
        this.customerForm.controls['addressOneCtrl'].setValue(this.selectedCustomer.address1);
        this.customerForm.controls['addressTwoCtrl'].setValue(this.selectedCustomer.address2);
        this.customerForm.controls['freightCtrl'].setValue(this.selectedCustomer.freightSpend);
        this.customerForm.controls['creditLimitAmountCtrl'].setValue(this.selectedCustomer.creditLimitAmount);
        this.customerForm.controls['revenueBaseLineCtrl'].setValue(this.selectedCustomer.monthlyNetRevenueBaseLine);
        this.customerStatusId = this.selectedCustomer.customerStatusId;
        var status = this.customerStatusList.find(t => t.customerStatusId == this.selectedCustomer.customerStatusId);
        if (status != null) {
            this.customerStatus = status.description;
        }
        this.customerForm.controls['statusCtrl'].setValue(this.customerStatusId);

        if (this.selectedCustomer.parent == 'Y') this.customerForm.controls['isParentCtrl'].setValue(true);
    }

    public saveCustomer(isERPSync: boolean) {
        this.submitted = true;

        if (this.customerForm.valid) {
            var selectedCity = this.customerForm.controls['cityCtrl'].value;

            if (typeof selectedCity === 'string') {
                this.customerForm.controls['cityCtrl'].setValue('');
                this.customerForm.controls['cityCtrl'].setErrors({ 'required': true});
                return;
            }

            var statusId = this.customerStatusId;
            if (this.customerForm.controls['statusCtrl'].value) {
                statusId = parseInt(this.customerForm.controls['statusCtrl'].value);
            }


            this.isSavingCustomer = true;
            var employeeId = parseInt(localStorage.getItem('EmployeeId')!);
            var customer = {
                customerID: this.selectedCustomerId,
                name: this.customerForm.controls['nameCtrl'].value,
                salesEmployeeID: this.customerForm.controls['salesRepCtrl'].value,
                opsRepId: this.customerForm.controls['opsRepCtrl'].value,
                accountRepId: this.customerForm.controls['accountRepCtrl'].value,
                customerStatusId: statusId,
                address1: this.customerForm.controls['addressOneCtrl'].value,
                cityId: (this.customerForm.controls['cityCtrl'].value as CityDropdown).cityId,
                apiZip: this.customerForm.controls['zipCtrl'].value,
                billToInd: this.customerForm.controls['billToIndCtrl'].value,
                shipperInd: this.customerForm.controls['shipperIndCtrl'].value,
                consigneeInd: this.customerForm.controls['consigneeIndCtrl'].value,
                activeInd: this.customerForm.controls['activeIndCtrl'].value,
                retiredInd: this.customerForm.controls['retiredIndCtrl'].value,
                freightSpend: this.customerForm.controls['freightCtrl'].value,
                creditLimitAmount: this.customerForm.controls['creditLimitAmountCtrl'].value,
                parent: this.customerForm.controls['isParentCtrl'].value == true ? 'Y' : 'N',
                monthlyNetRevenueBaseLine: this.customerForm.controls['revenueBaseLineCtrl'].value
            } as Customer;

            if (this.crmNotes.commentId && this.crmNotes.commentId > 0) {
                customer.commentId = this.crmNotes.commentId;
            }
            if (this.operationalNotes.commentId && this.operationalNotes.commentId > 0) {
                customer.extraCommentId = this.operationalNotes.commentId;
            }

            if (customer.customerID < 1) {
                customer.createDate = new Date();
                customer.createEmployeeId = employeeId;
            } else {
                customer.lastRevisedEmployeeId = employeeId;
            }

            this.crmService.saveCustomer(customer).subscribe(result => {                         
                var savedCustomer = result;

                if (isERPSync) {
                    this.syncERPCustomer(savedCustomer.customerID);
                }

                if (savedCustomer.customerID > 0) {
                    this.isSavingCustomer = false;
                    let maps: CustomerInitiativeMap[] = [];

                    if (this.customerForm.controls['initiativesCtrl'].value != '') {
                        var inits = this.customerForm.controls['initiativesCtrl'].value as number[];

                        inits.forEach(i => {
                            var map = {
                                customerID: savedCustomer.customerID,
                                customerInitiativeID: i,
                                customerInitiativeMapID: 0
                            } as CustomerInitiativeMap;

                            maps.push(map);
                        });

                        this.crmService.saveCustomerInitiativeMaps(maps).subscribe(result => {

                        });
                    } else {
                        this.crmService.deleteCustomerInitiativeMaps(savedCustomer.customerID).subscribe(result => {

                        });
                    }

                                        

                    this.isSavingContact = true;
                    var contactName = this.customerForm.controls['contactNameCtrl'].value;
                    this.saveMessage = 'Successfully created Customer: ' + savedCustomer.customerCode + '.  Saving default Contact: ' + contactName;

                    var defaultContact = {
                        contactID: this.selectedContactId,
                        contactType: 'Customer',
                        tableID: savedCustomer.customerID,
                        firstName: contactName.split(' ')[0],
                        lastName: contactName.replace(contactName.split(' ')[0], ''),
                        defaultContactInd: true,
                        phone: this.customerForm.controls['contactPhoneCtrl'].value,
                        email: this.customerForm.controls['contactEmailCtrl'].value
                    } as Contact;

                    this.contactService.saveContact(defaultContact).subscribe(c => {
                        this.isSavingContact = false;
                        this.saveMessage = 'Successfully created Customer: ' + savedCustomer.customerCode + '.  Created Contact: ' + contactName;
                        this.savedCustomer.emit(savedCustomer);
                    });
                } else {
                    this.submitted = false;
                    this.saveMessage = 'Failed to save Customer';
                }
            });
        }
    }

    private getComments() {
        if (this.selectedCustomer != null) {
            if (this.selectedCustomer.commentId > 0) {
                this.commentService.getCommentById(this.selectedCustomer.commentId).subscribe(result => {
                    this.customerForm.controls['crmNotesCtrl'].setValue(result.commentText);
                    this.crmNotes = result;
                });
            } else {
                this.customerForm.controls['crmNotesCtrl'].setValue("");
                this.crmNotes = {} as Comment;
            }

            if (this.selectedCustomer.commentId > 0) {
                this.commentService.getCommentById(this.selectedCustomer.extraCommentId).subscribe(result => {
                    this.customerForm.controls['operationalNotesCtrl'].setValue(result.commentText);
                    this.operationalNotes = result;
                });
            } else {
                this.operationalNotes = {} as Comment;
                this.customerForm.controls['operationalNotesCtrl'].setValue("");
            }
        }
    }

    //public saveQuoteOrderLineComments() {
    //    this.crmNotes.commentText = this.searchQuoteOrderLine.notes;
    //    this.operationalNotes.commentText = this.searchQuoteOrderLine.remarks;
    //    this.saveComment(this.notes);
    //    this.saveComment(this.remarks);
    //}

    public saveComment(comment: Comment, commentType: string) {
        if (comment == null || comment.commentText == undefined) {
            return;
        }
        
        if (comment.commentText.length < 1) {
            comment.commentText = "-";
        }

        this.commentService.saveComment(comment).subscribe(result => {
            var ok = result;
            if (commentType == "crmNotes") {
                this.selectedCustomer.commentId = result.commentId;
                this.crmService.saveCustomerComments(this.selectedCustomer.customerID, this.selectedCustomer.commentId, this.selectedCustomer.extraCommentId).subscribe(resultComment => {
                    console.log("saveCustomerComments", result);
                });
                this.crmNotes = Object.assign({}, result);
            }
            else if (commentType == "operationalNotes") {
                this.selectedCustomer.extraCommentId = result.commentId;
                this.crmService.saveCustomerComments(this.selectedCustomer.customerID, this.selectedCustomer.commentId, this.selectedCustomer.extraCommentId).subscribe(resultComment => {
                    console.log("saveCustomerComments", result);
                });
                this.operationalNotes = Object.assign({}, result);
            }
        });
    }

    public syncERPCustomer(customerID) {
        this.notificationService.showInfo("Updating ERP Customer ...", "ERP Update");
        this.erpService.updateERPCustomer(customerID, "TMW").subscribe(result => {
            var ok = result;
            if (result.message != "OK") {
                this.notificationService.showError("Error updating ERP: " + result.message, "ERROR: ERP Update");
            }
            else {
                this.notificationService.showInfo("ERP Update Complete", "ERP Update");
            }
        });
    }

    saveDefaultContact() {

    }

    public hasSales(): boolean {
        var hasSales = false;

        if (this.financials.length > 0) {
            this.financials.forEach((cf, index) => {
                if (cf.ytdGrossRevenue > 0) hasSales = true;
            });
        }

        return hasSales;
    }

    public leaveZip() {
        if (this.selectedCustomerId != null && this.selectedCustomerId > 0) return;

        var city = this.customerForm.controls['cityCtrl'].value;
        var zip = this.customerForm.controls['zipCtrl'].value;
        var address = this.customerForm.controls['addressOneCtrl'].value;

        if (city == null || city == '' || zip == null || zip == '' || address == null || address == '') return;

        this.crmService.getCustomerByAddressCityStateZip(address, city.name, city.stAbbr, zip).subscribe(result => {
            if (result != null) {
                //warn user of matching customer, disable save button and redirect them
                alert('A customer already exists at this location.  You will be redirected to the profile page.');

                this.savedCustomer.emit(undefined);
                this.router.navigate(['customer-detail', result.customerCode]);
            }
        });
    }
}

@Component({
    selector: 'add-customer-dialog',
    templateUrl: './dialog.component.html',
    standalone: false
})
export class AddCustomerDialog {
    constructor(public dialogRef: MatDialogRef<AddCustomerDialog>, @Inject(MAT_DIALOG_DATA) public data: any) { }

    cancel(): void {
        this.dialogRef.close();
    }

    savedCustomer(customer: Customer) {
        this.dialogRef.close({ customer: customer });
    }
}
